import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import { mapCandidateAttachments } from 'features/sendouts/common/utils';
import { EmailDetails, SendoverReasonsFields } from 'features/sendouts/components/forms';
import { AutomaticReminder } from 'features/sendouts/components/forms/AutomaticReminder';
import {
  AccountableRecruiterSelector,
  Attachments,
  CandidateDetailsFields
} from 'features/sendouts/components/shared';
import { ATTACHMENTS_CATEGORIES } from 'features/sendouts/constants';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { Permissions, userHasPermission } from 'services/Authorization';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import NumberedForm from 'UI/components/molecules/NumberedForm';
import { CandidateFileCategoriesWithSendout } from 'UI/components/organisms/sendouts/utils';
import { FeatureFlags } from 'UI/constants/featureFlags';

import { NumberedFormWrapper } from '../../CreateSendoutDrawer/SendoutForm/SendoutForm.styled';

import { FORM_FIELDS_MAP } from './SendoverForm.constants';

export const SendoverForm = ({ jobOrder = {} }) => {
  const [candidateAttachments, setCandidateAttachments] = useState(null);
  const [candidateInfo, setCandidateInfo] = useState(null);

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isAutomaticReminderEnabled = checkIfFeatureFlagEnabled(FeatureFlags.SendoverHAReminder);

  const { watch } = useFormContext();

  const formValues = watch([
    FORM_FIELDS_MAP.AccountableRecruiter.key,
    FORM_FIELDS_MAP.Candidate.key,
    FORM_FIELDS_MAP.CandidateRecruiter.key,
    FORM_FIELDS_MAP.SendoverReasons.key
  ]);

  const accountableRecruiterValue = formValues[FORM_FIELDS_MAP.AccountableRecruiter.key];
  const candidateValue = formValues[FORM_FIELDS_MAP.Candidate.key];
  const candidateRecruiterValue = formValues[FORM_FIELDS_MAP.CandidateRecruiter.key];
  const sendoverReasonsValue = formValues[FORM_FIELDS_MAP.SendoverReasons.key];

  const shouldDisplayAutomaticReminderCheckbox =
    isAutomaticReminderEnabled &&
    userHasPermission(Permissions.Sendouts.CanOverrideSendoverHAFollowUp) &&
    candidateValue;

  const shouldDisplayAutomaticReminderCopy = isAutomaticReminderEnabled && candidateValue;

  const accountableSelectorOptions = [
    {
      ...jobOrder?.recruiter,
      fullName: jobOrder?.recruiter?.personalInformation?.full_name
    }
  ]; // TODO: apply logic to get specific options according to user role

  const handleOnSelectCandidate = candidate => {
    const formattedFiles = mapCandidateAttachments(candidate?.files); // TODO: this mapping function is needed to convert the candidate files in the expected format for Attachments component. We can get rid of it when the sendouts api v2 is ready and the candidate files are coming with file_type_id field directly.
    setCandidateAttachments(formattedFiles);
    setCandidateInfo({
      name: candidate?.personalInformation?.first_name,
      phone: candidate?.personalInformation?.contact?.phone
    });
  };

  return (
    <Grid container>
      {accountableSelectorOptions.length > 0 && (
        <AccountableRecruiterSelector options={accountableSelectorOptions} />
      )}
      <NumberedFormWrapper item xs={12}>
        <NumberedForm>
          <When condition={accountableRecruiterValue}>
            <NumberedForm.Item title={strings.sendoutsV2.form.sections.candidateDetails.title}>
              <CandidateDetailsFields onSelectCandidate={handleOnSelectCandidate} />
            </NumberedForm.Item>
          </When>
          <When condition={candidateValue && candidateRecruiterValue}>
            <NumberedForm.Item title={strings.sendoutsV2.form.sections.sendoverReasons.title}>
              <SendoverReasonsFields />
            </NumberedForm.Item>
          </When>
          <When condition={sendoverReasonsValue?.length > 0}>
            <NumberedForm.Item title={strings.sendoutsV2.form.sections.attachments.title}>
              <Attachments
                categories={[ATTACHMENTS_CATEGORIES.resume, ATTACHMENTS_CATEGORIES.other]}
                categoryIdMap={CandidateFileCategoriesWithSendout}
                files={candidateAttachments || []}
              />
            </NumberedForm.Item>
            <NumberedForm.Item title={strings.sendoutsV2.form.sections.emailDetails.title}>
              <AutomaticReminder
                shouldDisplayAutomaticReminderCheckbox={shouldDisplayAutomaticReminderCheckbox}
                shouldDisplayAutomaticReminderCopy={shouldDisplayAutomaticReminderCopy}
              />
              <EmailDetails jobOrder={jobOrder} candidateInfo={candidateInfo} />
            </NumberedForm.Item>
          </When>
        </NumberedForm>
      </NumberedFormWrapper>
    </Grid>
  );
};
